import { FreeCoffeeType, FreeStuffInfo, FreeStuffStatus } from 'aven_types'
import { assertNever } from '../assertNever'
import { inspect } from 'aven_shared/src/utils/logger'
import { log } from '@/utils/logger'
import { DateTime } from 'luxon'

const STARBUCKS_COFFEE_IMAGE_SRC = require('@/assets/images/starbucks-coffee-v2.png')
const DUNKIN_COFFEE_IMAGE_SRC = require('@/assets/images/dunkin-coffee-v2.png')

export enum FreeCofeeCallToActionType {
    CONNECT_PLAID = 'connect_plaid',
    CLAIM_FREE_COFFEE = 'claim_free_coffee',
    JOIN_THE_WAITLIST = 'join_the_waitlist',
    SHARE_WITH_YOUR_FRIENDS = 'share_with_your_friends',
    SHOW_INELIGIBILITY_REASONS = 'show_ineligibility_reasons',
    NO_ACTION = 'no_action',
}

export type FreeCoffeeCopy = {
    title: string
    description: string | null
    callToAction: string | null
    callToActionType: FreeCofeeCallToActionType
    shortenedFootnote: string
}

type FreeCoffeeCopyWithoutShortenedFootnote = Omit<FreeCoffeeCopy, 'shortenedFootnote'>

export type FreeCoffeeAssetsAndCopy = {
    imageSrc: string
    copy: FreeCoffeeCopy
}

export function getFreeCoffeeAssetsAndCopy(freeCoffeeType: FreeCoffeeType, freeStuffInfo: FreeStuffInfo | null): FreeCoffeeAssetsAndCopy {
    const imageSrc = getImageSrcFromFreeCoffeeType(freeCoffeeType)

    if (!freeStuffInfo) {
        log.info('getFreeCoffeeAssetsAndCopy => No freeStuffInfo found, we are likely still retrieving it, returning loading assets and copy')
        return {
            imageSrc,
            copy: {
                ...createFreeCoffeeCheckingEligibilityCopy(freeCoffeeType),
                shortenedFootnote: createFreeCoffeeShortenedFootnote(freeCoffeeType),
            },
        }
    }

    const copyWithoutShortenedFootnote = getFreeCoffeeCopyWithoutShortenedFootnoteFromFreeStuffInfo(freeCoffeeType, freeStuffInfo)
    const assetsAndCopy = {
        imageSrc,
        copy: {
            ...copyWithoutShortenedFootnote,
            shortenedFootnote: createFreeCoffeeShortenedFootnote(freeCoffeeType),
        },
    }
    log.info('getFreeCoffeeAssetsAndCopy => assetsAndCopy: %{assetsAndCopyInspect}', { assetsAndCopyInspect: inspect(assetsAndCopy) })
    return assetsAndCopy
}

function getImageSrcFromFreeCoffeeType(freeCoffeeType: FreeCoffeeType): string {
    let imageSrc: string
    switch (freeCoffeeType) {
        case FreeCoffeeType.STARBUCKS:
            imageSrc = STARBUCKS_COFFEE_IMAGE_SRC
            break
        case FreeCoffeeType.DUNKIN:
            imageSrc = DUNKIN_COFFEE_IMAGE_SRC
            break
        default:
            return assertNever(freeCoffeeType, `Unexpected freeCoffeeType: ${freeCoffeeType}`)
    }
    log.info('getImageSrcFromFreeCoffeeType => freeCoffeeType: %{freeCoffeeType}, imageSrc: %{imageSrc}', { freeCoffeeType, imageSrc })
    return imageSrc
}

function getFreeCoffeeCopyWithoutShortenedFootnoteFromFreeStuffInfo(freeCoffeeType: FreeCoffeeType, freeStuffInfo: FreeStuffInfo): FreeCoffeeCopyWithoutShortenedFootnote {
    const freeStuffStatusInfo = freeStuffInfo.freeStuffStatusInfo
    const status = freeStuffStatusInfo.status
    switch (status) {
        case FreeStuffStatus.FREE_COFFEE_CAN_CLAIM_NOW:
            return createFreeCoffeeCanClaimNowCopy(freeCoffeeType, null)
        case FreeStuffStatus.FREE_COFFEE_INELIGIBLE:
            return createFreeCoffeeIneligibleCopy(freeCoffeeType)
        case FreeStuffStatus.FREE_COFFEE_PLAID_NOT_CONNECTED:
            return createFreeCoffeeGenericConnectPlaidCopy(freeCoffeeType)
        case FreeStuffStatus.FREE_COFFEE_CONNECT_PLAID_AGAIN:
            return createFreeCoffeeGenericConnectPlaidCopy(freeCoffeeType)
        case FreeStuffStatus.FREE_COFFEE_VANTAGE_SCORE_TOO_LOW:
            return createFreeCoffeeIneligibleCopy(freeCoffeeType)
        case FreeStuffStatus.FREE_COFFEE_CLAIMING_TOO_SOON:
            return createFreeCoffeeCooldownCopy(freeCoffeeType, freeStuffStatusInfo.content.nextFreeCoffeeDate, null)
        case FreeStuffStatus.FREE_COFFEE_CAN_JOIN_WAITLIST:
            return freeCoffeeJoinWaitlistCopy
        case FreeStuffStatus.FREE_COFFEE_IN_WAITLIST:
            return createFreeCoffeeJoinWaitlistCopy()
        case FreeStuffStatus.FREE_COFFEE_DTI_TOO_HIGH:
            return createFreeCoffeeIneligibleCopy(freeCoffeeType)
        case FreeStuffStatus.FREE_COFFEE_EXPERIAN_FROZEN:
            return createFreeCoffeeIneligibleCopy(freeCoffeeType) // TODO: Add unfreeze experian functionality later
        case FreeStuffStatus.FREE_COFFEE_CONNECT_PLAID_TO_VERIFY_INCOME:
            return {
                title: `Free ${freeCoffeeType} Every Week`,
                description: 'Connect your accounts to verify that you have $100+ income.',
                callToAction: 'Connect Accounts',
                callToActionType: FreeCofeeCallToActionType.CONNECT_PLAID,
            }
        case FreeStuffStatus.FREE_COFFEE_CONNECT_PLAID_AGAIN_TO_VERIFY_INCOME:
            return {
                title: 'Almost there!',
                description: 'Connect more accounts to verify that you have $100+ income.',
                callToAction: 'Connect Accounts',
                callToActionType: FreeCofeeCallToActionType.CONNECT_PLAID,
            }
        case FreeStuffStatus.FREE_COFFEE_CAN_CLAIM_NOW_WITH_WEEKS_LEFT:
            return createFreeCoffeeCanClaimNowCopy(freeCoffeeType, freeStuffStatusInfo.content.weeksOfFreeCoffeeLeft)
        case FreeStuffStatus.FREE_COFFEE_CLAIMING_TOO_SOON_WITH_WEEKS_LEFT:
            return createFreeCoffeeCooldownCopy(freeCoffeeType, freeStuffStatusInfo.content.nextFreeCoffeeDate, freeStuffStatusInfo.content.weeksOfFreeCoffeeLeft)
        case FreeStuffStatus.FREE_COFFEE_ALL_FREE_COFFEE_ASSIGNED_CLAIMED:
            return {
                title: `You’re in the ${freeCoffeeType} raffle!`,
                description: `We’ll notify you if you’re selected to get free ${freeCoffeeType}.`,
                callToAction: 'Share With Your Friends',
                callToActionType: FreeCofeeCallToActionType.SHARE_WITH_YOUR_FRIENDS,
            }
        case FreeStuffStatus.FREE_COFFEE_INSUFFICIENT_INFO_TO_DETERMINE_ELIGIBLITY:
            return createFreeCoffeeIneligibleCopy(freeCoffeeType)
        case FreeStuffStatus.FREE_COFFEE_DETERMINING_ELIGIBLITY:
            return createFreeCoffeeCheckingEligibilityCopy(freeCoffeeType)
        default:
            return assertNever(status, `Unhandled freeStuffStatus: ${status}`)
    }
}

const freeCoffeeJoinWaitlistCopy: FreeCoffeeCopyWithoutShortenedFootnote = {
    title: 'Join the waitlist',
    description: 'Get on the waitlist today and we’ll notify you as soon as this promotion is available for you!',
    callToAction: 'Join the waitlist',
    callToActionType: FreeCofeeCallToActionType.JOIN_THE_WAITLIST,
}

function createFreeCoffeeCheckingEligibilityCopy(freeCoffeeType: FreeCoffeeType): FreeCoffeeCopyWithoutShortenedFootnote {
    return {
        title: `Checking eligibility for free ${freeCoffeeType}`,
        description: null,
        callToAction: null,
        callToActionType: FreeCofeeCallToActionType.NO_ACTION,
    }
}

function createFreeCoffeeCanClaimNowCopy(freeCoffeeType: FreeCoffeeType, remainingWeeksOfFreeCoffee: number | null): FreeCoffeeCopyWithoutShortenedFootnote {
    const description = remainingWeeksOfFreeCoffee ? `Get ${freeCoffeeType} on us for ${remainingWeeksOfFreeCoffee} more weeks.` : `Get ${freeCoffeeType} on us.`
    return {
        title: `Claim your ${freeCoffeeType} now`,
        description,
        callToAction: `Claim My Free ${freeCoffeeType}`,
        callToActionType: FreeCofeeCallToActionType.CLAIM_FREE_COFFEE,
    }
}

function createFreeCoffeeGenericConnectPlaidCopy(freeCoffeeType: FreeCoffeeType): FreeCoffeeCopyWithoutShortenedFootnote {
    return {
        title: `Free ${freeCoffeeType} Every Week`,
        description: `Connect your accounts to determine your eligibility.`,
        callToAction: `Connect Accounts`,
        callToActionType: FreeCofeeCallToActionType.CONNECT_PLAID,
    }
}

function createFreeCoffeeCooldownCopy(freeCoffeeType: FreeCoffeeType, nextFreeCoffeeDate: Date | string, remainingWeeksOfFreeCoffee: number | null): FreeCoffeeCopyWithoutShortenedFootnote {
    const timeUntilNextFreeCoffeeCopy = createTimeUntilNextFreeCoffeeCopy(nextFreeCoffeeDate)
    const description = remainingWeeksOfFreeCoffee ? `Get ${freeCoffeeType} on us for ${remainingWeeksOfFreeCoffee} more weeks.` : `Get ${freeCoffeeType} on us.`
    return {
        title: `Your next coffee is in ${timeUntilNextFreeCoffeeCopy}`,
        description,
        callToAction: `Share With Your Friends`,
        callToActionType: FreeCofeeCallToActionType.SHARE_WITH_YOUR_FRIENDS,
    }
}

function createFreeCoffeeJoinWaitlistCopy(): FreeCoffeeCopyWithoutShortenedFootnote {
    return {
        title: 'Join the waitlist',
        description: 'Get on the waitlist today and we’ll notify you as soon as this promotion is available for you!',
        callToAction: 'Join the waitlist',
        callToActionType: FreeCofeeCallToActionType.JOIN_THE_WAITLIST,
    }
}

function createFreeCoffeeIneligibleCopy(freeCoffeeType: FreeCoffeeType): FreeCoffeeCopyWithoutShortenedFootnote {
    return {
        title: `Not eligible yet`,
        description: `You are currently not eligible for free ${freeCoffeeType}.`,
        callToAction: `Learn More`,
        callToActionType: FreeCofeeCallToActionType.SHOW_INELIGIBILITY_REASONS,
    }
}

function createFreeCoffeeShortenedFootnote(freeCoffeeType: FreeCoffeeType): string {
    return `By clicking above, you accept this ${freeCoffeeType} Coffee Promotion invitation and the ${freeCoffeeType}...`
}

function createTimeUntilNextFreeCoffeeCopy(nextFreeCoffeeDate: Date | string): string {
    const now = DateTime.now()
    // Handle both Date objects and ISO date strings
    const nextDate = typeof nextFreeCoffeeDate === 'string' ? DateTime.fromISO(nextFreeCoffeeDate) : DateTime.fromISO(nextFreeCoffeeDate.toISOString())
    const diff = nextDate.diff(now)

    const days = Math.floor(diff.as('days'))
    const hours = Math.floor(diff.as('hours'))
    const minutes = Math.floor(diff.as('minutes'))

    if (days >= 1) {
        return days === 1 ? '1 day' : `${days} days`
    } else if (hours >= 1) {
        return hours === 1 ? '1 hour' : `${hours} hours`
    } else {
        return minutes === 1 ? '1 minute' : `${minutes} minutes`
    }
}
